import { isMongoId } from "validator";
import { z } from "zod";

import { AI_URL } from "../config/env";
import { smartFetch } from "../utils/http";
import { parseResponse } from "../utils/parseResponse";

export const askQAQuestionRequestParser = z
  .object({
    query: z.string(),
  })
  .strict();

export type AskQAQuestionRequest = z.infer<typeof askQAQuestionRequestParser>;

export const askQAQuestionResponseParser = z
  .object({
    answer: z.string(),
  })
  .strict();

export type AskQAQuestionResponse = z.infer<typeof askQAQuestionRequestParser>;

export const reviewTopicsResponseParser = z.object({
  topics: z.array(z.string()),
});

export type ReviewTopicsResponse = z.infer<typeof reviewTopicsResponseParser>;

export async function askQAQuestion(payload: AskQAQuestionRequest) {
  const json = await smartFetch(
    `${AI_URL}/api/v1/qa`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    true,
  );

  const parsedData = askQAQuestionResponseParser.parse(json);
  return parsedData;
}

export const reviewStreamPayloadParser = z.object({
  ticketId: z.string().refine((val) => isMongoId(val)),
  boardId: z.string().refine((val) => isMongoId(val)),
  personas: z.array(z.enum(["brand", "copy", "legal"])),
});

type ReviewStreamPayload = z.infer<typeof reviewStreamPayloadParser>;

const reviewCommentParser = z.object({
  _id: z.string().refine((val) => isMongoId(val)),
  comment: z.string().describe("The comment text"),
  persona: z.enum(["brand", "copy", "legal"]),
  required: z
    .boolean()
    .describe(
      "Whether or not the comment is a required change or a suggestion",
    ),
  x: z.number().positive(),
  y: z.number().positive(),
  page_num: z.number().positive(),
  timestamp: z.number().nonnegative(),
});

export const reviewStreamResponseParser = z.object({
  status: z.string(),
  isDone: z.boolean(),
  pendingComments: z.array(reviewCommentParser),
  finalComments: z.array(reviewCommentParser),
});

type ReviewStreamResponse = z.infer<typeof reviewStreamResponseParser>;

export async function createReviewStream(
  payload: ReviewStreamPayload,
  onMessage?: (chunk: {
    id: number;
    event: string;
    data: ReviewStreamResponse;
  }) => void,
) {
  const response = await fetch(`${AI_URL}api/v1/reviews/stream`, {
    headers: {
      authorization: `Bearer ${localStorage.getItem("token")}`,
      "Content-Type": "application/json",
    },
    method: "POST",
    mode: "cors",
    credentials: "include",
    body: JSON.stringify({
      ticket_id: payload.ticketId,
      board_id: payload.boardId,
      personas: payload.personas,
    }),
  });

  const reader = response.body?.getReader();
  const readChunk = (): Promise<void> => {
    if (!reader) throw new Error("No reader");
    return reader.read().then(({ value, done }) => {
      if (done) {
        console.debug("Stream finished.");
        return Promise.resolve();
      }

      const chunkString = new TextDecoder().decode(value);

      // Parse messages from SSE format. Do they need to be in SSE format? Probably not. But they are.
      const messages = chunkString.trim().split("\n\n");
      for (const message of messages) {
        const fields = message.split("\n").reduce((acc, l) => {
          const i = l.indexOf(": ");
          acc[l.slice(0, i)] = l.slice(i + 2);
          return acc;
        }, Object.create(null));
        if (fields.data) {
          try {
            fields.data = JSON.parse(fields.data);
          } catch (err) {
            console.error("Non-JSON data", err);
          }
        }
        if (fields.id && /^0$|^[1-9][0-9]{0,14}$/.test(fields.id)) {
          fields.id = parseInt(fields.id, 10);
        }
        onMessage?.(fields); // Notify subscribers
      }

      return readChunk();
    });
  };

  return readChunk();
}

const summarizeFolderResponseParser = z.object({
  summary: z.string(),
});

export async function summarizeFolder(folderId: string) {
  const json = await smartFetch(
    `${AI_URL}api/v1/reviews/summarize-folder`,
    {
      method: "POST",
      body: JSON.stringify({
        folder_id: folderId,
      }),
    },
    true,
  );

  const parsedJson = parseResponse(summarizeFolderResponseParser, json);

  return parsedJson;
}

export async function getReviewTopics(personas: string[]) {
  const queryParams = new URLSearchParams();
  personas.forEach((persona) => {
    queryParams.append("personas", persona);
  });

  const json = await smartFetch(
    `${AI_URL}api/v1/reviews/topics?${queryParams.toString()}`,
    {
      method: "GET",
    },
    true,
  );

  const parsedJson = parseResponse(reviewTopicsResponseParser, json);

  return parsedJson?.topics ?? [];
}
